// src/i18n/translations.js

export const translations = {
  en: {
    // Navigation
    nav: {
      features: 'Features',
      howItWorks: 'How It Works',
      industries: 'Industries',
      pricing: 'Pricing',
      login: 'Log In',
      getStarted: 'Get Started',
      dashboard: 'Dashboard',
      generateCOA: 'Generate COA',
      certificates: 'Certificates',
      settings: 'Settings',
      logout: 'Log out',
      settings: 'Settings',
    },

    // Home
    home: {
      hero: {
        title: 'Certificate of Analysis',
        titleAccent: 'Generator',
        subtitle: 'Create accurate, professional and export-ready COA documents in seconds.',
        cta: 'Generate Free COA',
        sample: 'View Sample Certificate',
        trusted: 'Trusted by laboratories and companies worldwide',
      },
      steps: {
        title: 'Generate your COA in 3 simple steps',
        s1t: 'Enter Chemical Info',
        s1d: 'Add basic product details and batch information.',
        s2t: 'Add Test Results',
        s2d: 'Fill in specifications and laboratory results.',
        s3t: 'Preview & Download',
        s3d: 'Get your COA as PDF or Word instantly.',
      },
      features: {
        title: 'Everything you need for professional COAs',
        f1t: 'Chemical Database',
        f1d: 'CAS lookup, molecular formula, molecular weight, chemical structure.',
        f2t: 'Smart Templates',
        f2d: 'Pharmaceutical, Chemical, Industrial, Custom – export-ready COA.',
        f3t: 'Compliance Ready',
        f3d: 'GMP, USP, FDA, ICH compliant templates.',
        f4t: 'Digital Verification',
        f4d: 'QR validation, Certificate ID, Digital signature.',
        f5t: 'Custom Branding',
        f5d: 'Add your logo, custom colors, company info, professional look.',
        f6t: 'Secure & Reliable',
        f6d: 'Cloud secure, data encryption, backup & recovery, audit logs.',
      },
      industries: {
        title: 'Built for multiple industries',
        subtitle: 'Create COAs for any industry with specialized templates and compliant formats.',
        i1: 'Pharmaceutical',
        i2: 'Chemical Manufacturing',
        i3: 'Biotechnology',
        i4: 'Cosmetics',
        i5: 'Food & Beverage',
        i6: 'Agriculture',
      },
      pricing: {
        title: 'Simple, transparent pricing',
        subtitle: 'Choose the plan that works best for you.',
        free: 'Free',
        professional: 'Professional',
        enterprise: 'Enterprise',
        forever: 'Forever',
        month: '/month',
        custom: 'Custom',
        freeFeatures: ['5 COAs per month', 'PDF export', 'Standard templates', 'Standard support'],
        proFeatures: ['Unlimited COAs', 'PDF & Word export', 'Custom branding', 'QR verification', 'All templates', 'Priority support'],
        entFeatures: ['Everything in Professional', 'Custom workflow', 'Multiple users', 'Audit logs', 'Custom templates', 'Dedicated support'],
        getStarted: 'Get Started',
        startTrial: 'Start Free Trial',
        contactSales: 'Contact Sales',
        noCard: 'No credit card required',
        cancelAnytime: 'Cancel anytime',
      },
      footer: {
        rights: '© 2026 COA Lab. All rights reserved.',
        privacy: 'Privacy Policy',
        terms: 'Terms of Use',
        contact: 'Contact',
      },
    },

    // Auth
    auth: {
      login: {
        title: 'Welcome back!',
        subtitle: 'Sign in to your account',
        email: 'Email address',
        password: 'Password',
        forgot: 'Forgot password?',
        submit: 'Sign In',
        noAccount: "Don't have an account?",
        create: 'Create one',
        or: 'or continue with',
        google: 'Continue with Google',
      },
      register: {
        title: 'Create your account',
        subtitle: 'Start generating professional COAs today.',
        name: 'Full name',
        email: 'Email address',
        password: 'Password',
        confirm: 'Confirm password',
        terms: 'I agree to the Terms of Service and Privacy Policy',
        submit: 'Create Account',
        hasAccount: 'Already have an account?',
        signIn: 'Sign in',
      },
      reset: {
        title: 'Reset password',
        subtitle: 'Enter your email to receive reset instructions.',
        email: 'Email address',
        submit: 'Send Reset Link',
        back: 'Back to login',
        sent: 'Reset link sent! Check your inbox.',
      },
    },

    // COA Generator
    coa: {
      steps: {
        product: 'Product Information',
        specs: 'Specifications & Results',
        lab: 'Laboratory Information',
        preview: 'Preview & Download',
      },
      product: {
        title: 'Product Information',
        subtitle: 'Enter basic information about the product.',
        chemName: 'Chemical Name',
        casNumber: 'CAS Number',
        formula: 'Molecular Formula',
        weight: 'Molecular Weight',
        batch: 'Batch Number',
        mfgDate: 'Manufacturing Date',
        expDate: 'Expiration Date',
        appearance: 'Appearance',
        storage: 'Storage Conditions',
        country: 'Country of Origin',
        next: 'Next Step',
      },
      specs: {
        title: 'Specifications & Results',
        subtitle: 'Add the test specifications and obtained results.',
        parameter: 'Test / Parameter',
        specification: 'Specification',
        result: 'Result',
        unit: 'Unit',
        method: 'Method',
        addRow: '+ Add Row',
        back: 'Back',
        next: 'Next Step',
      },
      lab: {
        title: 'Laboratory Information',
        subtitle: 'Enter laboratory and analyst details.',
        labName: 'Laboratory Name',
        address: 'Address',
        testingMethod: 'Testing Method',
        analyst: 'Analyst',
        approvedBy: 'Approved By',
        approvalDate: 'Approval Date',
        signature: 'Digital Signature',
        back: 'Back',
        next: 'Next Step',
      },
      preview: {
        title: 'Preview & Download',
        docOptions: 'Document Options',
        certId: 'Certificate ID',
        language: 'Language',
        includeLogo: 'Include Logo',
        includeQR: 'QR Code',
        qrRequired: 'Always included (required)',
        downloadPDF: 'Download PDF',
        downloadWord: 'Download Word',
        downloadJSON: 'Download JSON',
        back: 'Back',
        save: 'Save Certificate',
      },
      success: {
        title: 'Your Certificate of Analysis has been generated successfully!',
        subtitle: 'Your download has started automatically.',
        another: 'Generate Another COA',
        dashboard: 'Go to Dashboard',
      },
      pending: {
        title: 'Your COA has been submitted for approval',
        subtitle: 'An administrator will review your certificate. You will be able to download it once approved.',
        badge: 'Awaiting Approval',
        viewStatus: 'View Status',
        submit: 'Submit for Approval',
        notice: 'Once submitted, your certificate will be reviewed by an administrator before it becomes available for download.',
        willBePending: 'PENDING REVIEW',
        statusPending: 'Pending Approval',
        statusApproved: 'Approved',
        statusRejected: 'Rejected',
        rejectionReason: 'Rejection reason',
        reviewedBy: 'Reviewed by',
        notDownloadable: 'Not available for download yet',
        waitingMsg: 'Your certificate is awaiting admin review.',
      },
    },

    // COA Wizard — dropdown options and labels
    wizard: {
      // Steps
      steps: { type: 'Type', contact: 'Contact', sample: 'Sample', analysis: 'Analysis', payment: 'Payment', review: 'Review' },
      // Step labels
      selectType: 'Select Certificate Type', selectTypeSub: 'Choose the type of analysis needed for your product.',
      contactTitle: 'Contact Information', contactSub: 'Your company details and request reference.',
      sampleTitle: 'Sample Information', sampleSub: 'Details about the product and sample being submitted.',
      analysisTitle: 'Analysis Requested', analysisSub: 'Select the analyses needed and enter the specification for each (if known).',
      paymentTitle: 'Payment', paymentSub: 'Send the payment and attach the transaction hash.',
      reviewTitle: 'Review & Submit', reviewSub: 'Review your request before submitting.',
      // Contact fields
      companyName: 'Name / Company Name', address: 'Address', requestDate: 'Request Date',
      // Sample sections
      sectionProduct: 'Product Identification', sectionDates: 'Dates', sectionOrigin: 'Origin',
      sectionClass: 'Classification', sectionStorage: 'Storage & Transport', sectionMethodology: 'Analytical Methodology',
      sectionLabel: '🏷️ Product Label',
      labelDesc: 'Upload a photo of the product label. It will appear in the PDF and on the verification page.',
      labelUpload: 'Upload product label', labelChange: 'Change label', labelUploaded: 'Label uploaded',
      labelHint: 'JPG, PNG · Max 5 MB · Click to select',
      // Sample fields
      productName: 'Product Name', lot: 'Lot / Batch', presentation: 'Presentation',
      pharmaceuticalForm: 'Pharmaceutical Form', sampleQty: 'Sample Quantity', sampleQtyPh: 'e.g. 5 vials',
      activeIngredient: 'Active Ingredient', doseConcentration: 'Dose / Concentration',
      sanitaryRegistry: 'Sanitary Registry #', mfgDate: 'Manufacturing Date', expDate: 'Expiration Date',
      samplingDate: 'Sampling Date (MP)', supplier: 'Supplier', manufacturer: 'Manufacturer / Country',
      productLine: 'Product Line', productCategory: 'Product Category',
      sampleTypeLabel: 'Sample Type (multi-select)', sampleTypeHint: 'MP=Raw material · PG=Bulk · PT=Finished product · DM=Medical device · MEE=Packaging · AP=Purified water · APT=Drinking water · CEA=Other',
      storageConditions: 'Storage Conditions', transportTemp: 'Transport Temperature',
      manufacturerCert: 'Manufacturer Certificate?',
      hasManufacturerCertYes: 'Yes', hasManufacturerCertNo: 'No',
      select: '-- Select --',
      // Presentations
      presentations: ['Tablets','Capsules','Solution','Suspension','Cream','Gel','Powder','Ampoule','Syrup','Ovules','Suppositories','Drops','Spray','Patch','Other'],
      pharmaForms: ['Solid','Liquid','Semi-solid','Gaseous','Other'],
      lines: ['Human','Veterinary','Agricultural'],
      lineValues: ['human','veterinary','agricultural'],
      categories: ['Medication','Cosmetic','Dietary Supplement','Phytotherapy','Homeopathic','Other'],
      categoryValues: ['medication','cosmetic','supplement','phytotherapy','homeopathic','other'],
      storageOpts: ['Ambient 25±5°C','Refrigeration 2–8°C','Light-sensitive'],
      storageValues: ['ambient','refrigerated','light-sensitive'],
      transportOpts: ['2–8°C','15–25°C','25±2°C','<–15°C','Ambient'],
      methodologies: ['USP','BP','EP','ISO','Client','Others'],
      // Analysis
      selected: 'selected', specPlaceholder: 'Specification...',
      obsLabel: 'Observations / Other requirements', obsPh: 'Additional information or special requirements...',
      // Payment
      amountLabel: 'Amount to pay', selectMethod: 'Payment method',
      sendToAddress: 'Send to this address', copy: 'Copy', copied: 'Copied!',
      network: 'Network', important: '⚠️ Important',
      importantNote: 'Send exactly the amount shown. Different amounts may be rejected.',
      txHashLabel: 'Transaction Hash (required)', txHashNote: 'Copy the hash from your wallet or exchange after sending.',
      txHashPh: 'Paste transaction hash here (e.g. 0x1a2b3c...)',
      screenshotLabel: 'Screenshot (optional)', screenshotNote: 'Attach payment confirmation as image.',
      attachScreenshot: '📎 Attach screenshot', changeScreenshot: '📎 Change',
      verifyOn: '🔍 Verify on',
      // Review
      summaryType: 'Type', summaryPrice: 'Price', summaryPayment: 'Payment', summaryTx: 'TX Hash',
      options: 'Options',
      // Cert type card
      popular: 'Popular', selectedLabel: 'Selected',
      // Navigation
      next: 'Next →', back: '←', submit: '📤 Submit for Approval',
    },

    // Dashboard
    dashboard: {
      title: 'Dashboard',
      welcome: 'Welcome back',
      total: 'COAs Generated',
      thisMonth: 'This month',
      recent: 'Recent Certificates',
      product: 'Product',
      batch: 'Batch',
      date: 'Date',
      status: 'Status',
      actions: 'Actions',
      viewAll: 'View all certificates',
      noData: 'No certificates yet. Generate your first COA!',
      generate: 'Generate COA',
      deleteConfirm: 'Are you sure you want to delete this certificate?',
    },

    // Common
    common: {
      loading: 'Loading...',
      error: 'An error occurred. Please try again.',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      view: 'View',
      download: 'Download',
      search: 'Search...',
      pass: 'PASS',
      fail: 'FAIL',
      required: 'This field is required',
    },
  },

  de: {
    nav: {
      features: 'Funktionen',
      howItWorks: 'Wie es funktioniert',
      industries: 'Branchen',
      pricing: 'Preise',
      login: 'Anmelden',
      getStarted: 'Loslegen',
      dashboard: 'Dashboard',
      generateCOA: 'ZA erstellen',
      certificates: 'Zertifikate',
      settings: 'Einstellungen',
      logout: 'Abmelden',
      settings: 'Einstellungen',
    },

    home: {
      hero: {
        title: 'Analysenzertifikat',
        titleAccent: 'Generator',
        subtitle: 'Erstellen Sie genaue, professionelle und exportfertige ZA-Dokumente in Sekunden.',
        cta: 'Kostenloses ZA erstellen',
        sample: 'Musterzertifikat ansehen',
        trusted: 'Vertraut von Labors und Unternehmen weltweit',
      },
      steps: {
        title: 'Erstellen Sie Ihr ZA in 3 einfachen Schritten',
        s1t: 'Chemische Info eingeben',
        s1d: 'Grundlegende Produktdetails und Chargeninformationen hinzufügen.',
        s2t: 'Testergebnisse hinzufügen',
        s2d: 'Spezifikationen und Laborergebnisse ausfüllen.',
        s3t: 'Vorschau & Download',
        s3d: 'Erhalten Sie Ihr ZA sofort als PDF oder Word.',
      },
      features: {
        title: 'Alles, was Sie für professionelle ZAs brauchen',
        f1t: 'Chemische Datenbank',
        f1d: 'CAS-Suche, Molekülformel, Molekulargewicht, chemische Struktur.',
        f2t: 'Intelligente Vorlagen',
        f2d: 'Pharmazeutisch, Chemisch, Industriell, Benutzerdefiniert – exportfertig.',
        f3t: 'Compliance-bereit',
        f3d: 'GMP, USP, FDA, ICH-konforme Vorlagen.',
        f4t: 'Digitale Verifizierung',
        f4d: 'QR-Validierung, Zertifikats-ID, Digitale Signatur.',
        f5t: 'Eigenes Branding',
        f5d: 'Eigenes Logo, benutzerdefinierte Farben, Unternehmensinfo.',
        f6t: 'Sicher & Zuverlässig',
        f6d: 'Cloud-sicher, Datenverschlüsselung, Backup & Wiederherstellung.',
      },
      industries: {
        title: 'Für mehrere Branchen entwickelt',
        subtitle: 'Erstellen Sie ZAs für jede Branche mit spezialisierten Vorlagen.',
        i1: 'Pharmazeutisch',
        i2: 'Chemische Herstellung',
        i3: 'Biotechnologie',
        i4: 'Kosmetik',
        i5: 'Lebensmittel & Getränke',
        i6: 'Landwirtschaft',
      },
      pricing: {
        title: 'Einfache, transparente Preise',
        subtitle: 'Wählen Sie den Plan, der am besten zu Ihnen passt.',
        free: 'Kostenlos',
        professional: 'Professionell',
        enterprise: 'Enterprise',
        forever: 'Für immer',
        month: '/Monat',
        custom: 'Individuell',
        freeFeatures: ['5 ZAs pro Monat', 'PDF-Export', 'Standardvorlagen', 'Standard-Support'],
        proFeatures: ['Unbegrenzte ZAs', 'PDF & Word Export', 'Eigenes Branding', 'QR-Verifizierung', 'Alle Vorlagen', 'Prioritäts-Support'],
        entFeatures: ['Alles in Professionell', 'Benutzerdefinierter Workflow', 'Mehrere Benutzer', 'Audit-Logs', 'Benutzerdefinierte Vorlagen', 'Dedizierter Support'],
        getStarted: 'Loslegen',
        startTrial: 'Kostenlos testen',
        contactSales: 'Vertrieb kontaktieren',
        noCard: 'Keine Kreditkarte erforderlich',
        cancelAnytime: 'Jederzeit kündbar',
      },
      footer: {
        rights: '© 2026 COA Lab. Alle Rechte vorbehalten.',
        privacy: 'Datenschutz',
        terms: 'Nutzungsbedingungen',
        contact: 'Kontakt',
      },
    },

    auth: {
      login: {
        title: 'Willkommen zurück!',
        subtitle: 'Melden Sie sich bei Ihrem Konto an',
        email: 'E-Mail-Adresse',
        password: 'Passwort',
        forgot: 'Passwort vergessen?',
        submit: 'Anmelden',
        noAccount: 'Noch kein Konto?',
        create: 'Jetzt erstellen',
        or: 'oder weiter mit',
        google: 'Mit Google fortfahren',
      },
      register: {
        title: 'Konto erstellen',
        subtitle: 'Starten Sie noch heute mit professionellen ZAs.',
        name: 'Vollständiger Name',
        email: 'E-Mail-Adresse',
        password: 'Passwort',
        confirm: 'Passwort bestätigen',
        terms: 'Ich stimme den Nutzungsbedingungen und der Datenschutzrichtlinie zu',
        submit: 'Konto erstellen',
        hasAccount: 'Bereits ein Konto?',
        signIn: 'Anmelden',
      },
      reset: {
        title: 'Passwort zurücksetzen',
        subtitle: 'Geben Sie Ihre E-Mail ein, um Anweisungen zu erhalten.',
        email: 'E-Mail-Adresse',
        submit: 'Reset-Link senden',
        back: 'Zurück zur Anmeldung',
        sent: 'Reset-Link gesendet! Prüfen Sie Ihren Posteingang.',
      },
    },

    coa: {
      steps: {
        product: 'Produktinformationen',
        specs: 'Spezifikationen & Ergebnisse',
        lab: 'Laborinformationen',
        preview: 'Vorschau & Download',
      },
      product: {
        title: 'Produktinformationen',
        subtitle: 'Grundlegende Informationen zum Produkt eingeben.',
        chemName: 'Chemischer Name',
        casNumber: 'CAS-Nummer',
        formula: 'Molekülformel',
        weight: 'Molekulargewicht',
        batch: 'Chargennummer',
        mfgDate: 'Herstellungsdatum',
        expDate: 'Ablaufdatum',
        appearance: 'Aussehen',
        storage: 'Lagerbedingungen',
        country: 'Herkunftsland',
        next: 'Nächster Schritt',
      },
      specs: {
        title: 'Spezifikationen & Ergebnisse',
        subtitle: 'Testspezifikationen und Ergebnisse hinzufügen.',
        parameter: 'Test / Parameter',
        specification: 'Spezifikation',
        result: 'Ergebnis',
        unit: 'Einheit',
        method: 'Methode',
        addRow: '+ Zeile hinzufügen',
        back: 'Zurück',
        next: 'Nächster Schritt',
      },
      lab: {
        title: 'Laborinformationen',
        subtitle: 'Labor- und Analytikerdetails eingeben.',
        labName: 'Laborname',
        address: 'Adresse',
        testingMethod: 'Testmethode',
        analyst: 'Analytiker',
        approvedBy: 'Genehmigt von',
        approvalDate: 'Genehmigungsdatum',
        signature: 'Digitale Signatur',
        back: 'Zurück',
        next: 'Nächster Schritt',
      },
      preview: {
        title: 'Vorschau & Download',
        docOptions: 'Dokumentoptionen',
        certId: 'Zertifikats-ID',
        language: 'Sprache',
        includeLogo: 'Logo einschließen',
        includeQR: 'QR-Code',
        qrRequired: 'Immer enthalten (erforderlich)',
        downloadPDF: 'PDF herunterladen',
        downloadWord: 'Word herunterladen',
        downloadJSON: 'JSON herunterladen',
        back: 'Zurück',
        save: 'Zertifikat speichern',
      },
      success: {
        title: 'Ihr Analysenzertifikat wurde erfolgreich erstellt!',
        subtitle: 'Ihr Download hat automatisch begonnen.',
        another: 'Weiteres ZA erstellen',
        dashboard: 'Zum Dashboard',
      },
      pending: {
        title: 'Ihr ZA wurde zur Genehmigung eingereicht',
        subtitle: 'Ein Administrator wird Ihr Zertifikat prüfen. Sie können es nach der Genehmigung herunterladen.',
        badge: 'Genehmigung ausstehend',
        viewStatus: 'Status ansehen',
        submit: 'Zur Genehmigung einreichen',
        notice: 'Nach dem Einreichen wird Ihr Zertifikat von einem Administrator geprüft, bevor es zum Download verfügbar ist.',
        willBePending: 'PRÜFUNG AUSSTEHEND',
        statusPending: 'Genehmigung ausstehend',
        statusApproved: 'Genehmigt',
        statusRejected: 'Abgelehnt',
        rejectionReason: 'Ablehnungsgrund',
        reviewedBy: 'Geprüft von',
        notDownloadable: 'Noch nicht zum Download verfügbar',
        waitingMsg: 'Ihr Zertifikat wartet auf die Prüfung durch einen Administrator.',
      },
    },

    wizard: {
      steps: { type: 'Typ', contact: 'Kontakt', sample: 'Probe', analysis: 'Analyse', payment: 'Zahlung', review: 'Überprüfung' },
      selectType: 'Zertifikatstyp wählen', selectTypeSub: 'Wählen Sie die benötigte Analyseart.',
      contactTitle: 'Kontaktinformationen', contactSub: 'Informationen zu Ihrem Unternehmen.',
      sampleTitle: 'Probendaten', sampleSub: 'Angaben zur Probe und zum Produkt.',
      analysisTitle: 'Angeforderte Analysen', analysisSub: 'Wählen Sie die gewünschten Analysen und geben Sie die Spezifikation ein.',
      paymentTitle: 'Zahlung', paymentSub: 'Überweisen Sie den Betrag und fügen Sie den Transaktions-Hash hinzu.',
      reviewTitle: 'Überprüfung & Einreichen', reviewSub: 'Überprüfen Sie Ihre Anfrage vor dem Absenden.',
      companyName: 'Name / Firmenname', address: 'Adresse', requestDate: 'Anfragedatum',
      sectionProduct: 'Produktidentifikation', sectionDates: 'Daten', sectionOrigin: 'Herkunft',
      sectionClass: 'Klassifikation', sectionStorage: 'Lagerung & Transport', sectionMethodology: 'Analysemethodik',
      sectionLabel: '🏷️ Produktetikett',
      labelDesc: 'Laden Sie ein Foto des Produktetiketts hoch. Es erscheint im PDF und auf der Verifizierungsseite.',
      labelUpload: 'Etikett hochladen', labelChange: 'Etikett ändern', labelUploaded: 'Etikett hochgeladen',
      labelHint: 'JPG, PNG · Max. 5 MB · Klicken zum Auswählen',
      productName: 'Produktname', lot: 'Charge / Lot', presentation: 'Darreichungsform',
      pharmaceuticalForm: 'Pharmazeutische Form', sampleQty: 'Probenanzahl', sampleQtyPh: 'z.B. 5 Fläschchen',
      activeIngredient: 'Wirkstoff', doseConcentration: 'Dosis / Konzentration',
      sanitaryRegistry: 'Zulassungsnummer', mfgDate: 'Herstellungsdatum', expDate: 'Ablaufdatum',
      samplingDate: 'Entnahmedatum (MP)', supplier: 'Lieferant', manufacturer: 'Hersteller / Land',
      productLine: 'Produktlinie', productCategory: 'Produktkategorie',
      sampleTypeLabel: 'Probentyp (Mehrfachauswahl)', sampleTypeHint: 'MP=Rohstoff · PG=Schüttgut · PT=Fertigprodukt · DM=Medizinprodukt · MEE=Verpackung · AP=Gereinigtes Wasser · APT=Trinkwasser · CEA=Sonstiges',
      storageConditions: 'Lagerbedingungen', transportTemp: 'Transporttemperatur',
      manufacturerCert: 'Herstellerzertifikat vorhanden?',
      hasManufacturerCertYes: 'Ja', hasManufacturerCertNo: 'Nein',
      select: '-- Auswählen --',
      presentations: ['Tabletten','Kapseln','Lösung','Suspension','Creme','Gel','Pulver','Ampulle','Sirup','Ovula','Zäpfchen','Tropfen','Spray','Pflaster','Sonstiges'],
      pharmaForms: ['Fest','Flüssig','Halbfest','Gasförmig','Sonstiges'],
      lines: ['Human','Veterinär','Agrar'],
      lineValues: ['human','veterinary','agricultural'],
      categories: ['Medikament','Kosmetikum','Nahrungsergänzung','Pflanzlich','Homöopathisch','Sonstiges'],
      categoryValues: ['medication','cosmetic','supplement','phytotherapy','homeopathic','other'],
      storageOpts: ['Umgebung 25±5°C','Kühlung 2–8°C','Lichtempfindlich'],
      storageValues: ['ambient','refrigerated','light-sensitive'],
      transportOpts: ['2–8°C','15–25°C','25±2°C','<–15°C','Umgebung'],
      methodologies: ['USP','BP','EP','ISO','Kunde','Andere'],
      selected: 'ausgewählt', specPlaceholder: 'Spezifikation...',
      obsLabel: 'Beobachtungen / Sonstige Anforderungen', obsPh: 'Zusätzliche Informationen...',
      amountLabel: 'Zu zahlender Betrag', selectMethod: 'Zahlungsmethode',
      sendToAddress: 'An diese Adresse senden', copy: 'Kopieren', copied: 'Kopiert!',
      network: 'Netzwerk', important: '⚠️ Wichtig',
      importantNote: 'Senden Sie genau den angezeigten Betrag. Abweichende Beträge können abgelehnt werden.',
      txHashLabel: 'Transaktions-Hash (erforderlich)', txHashNote: 'Kopieren Sie den Hash aus Ihrer Wallet nach dem Senden.',
      txHashPh: 'Transaktions-Hash hier einfügen (z.B. 0x1a2b3c...)',
      screenshotLabel: 'Screenshot (optional)', screenshotNote: 'Zahlungsnachweis als Bild beifügen.',
      attachScreenshot: '📎 Screenshot anhängen', changeScreenshot: '📎 Ändern',
      verifyOn: '🔍 Prüfen auf',
      summaryType: 'Typ', summaryPrice: 'Preis', summaryPayment: 'Zahlung', summaryTx: 'TX Hash',
      options: 'Optionen',
      popular: 'Beliebt', selectedLabel: 'Ausgewählt',
      next: 'Weiter →', back: '←', submit: '📤 Zur Genehmigung einreichen',
    },

    dashboard: {
      title: 'Dashboard',
      welcome: 'Willkommen zurück',
      total: 'Erstellte ZAs',
      thisMonth: 'Diesen Monat',
      recent: 'Aktuelle Zertifikate',
      product: 'Produkt',
      batch: 'Charge',
      date: 'Datum',
      status: 'Status',
      actions: 'Aktionen',
      viewAll: 'Alle Zertifikate anzeigen',
      noData: 'Noch keine Zertifikate. Erstellen Sie Ihr erstes ZA!',
      generate: 'ZA erstellen',
      deleteConfirm: 'Möchten Sie dieses Zertifikat wirklich löschen?',
    },

    common: {
      loading: 'Laden...',
      error: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
      save: 'Speichern',
      cancel: 'Abbrechen',
      delete: 'Löschen',
      edit: 'Bearbeiten',
      view: 'Ansehen',
      download: 'Herunterladen',
      search: 'Suchen...',
      pass: 'BESTANDEN',
      fail: 'NICHT BESTANDEN',
      required: 'Dieses Feld ist erforderlich',
    },
  },
};
